/**
 * statsUtils.js — Statistiques pures côté client, miroir du serveur (D200, D235).
 *
 * Copies de backend/utils/statsUtils.js (percentile, computeMinMaxAvg) et des
 * règles de backend/services/sessionStatsEnricher.js (pas médian, heures
 * au-dessus d'un seuil), car CRA interdit l'import hors de src/. Parité garantie
 * par backend/__tests__/statsUtilsParity.test.js et statsWindowParity.test.js,
 * qui exécutent ce fichier sur les mêmes vecteurs que le serveur : garder ce
 * module sans import et limité aux fonctions miroir.
 *
 * Fenêtrables : chaque fonction ne lit que les mesures reçues, jamais la
 * session entière. Les appliquer à des mesures filtrées donne les
 * statistiques de la fenêtre. Médiane = percentile(values, 50).
 */

export function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Percentile p (0-100) par interpolation linéaire entre rangs (numpy par défaut,
 * Excel CENTILE.INCLURE) : idx = p/100 × (n-1). Mesures absentes ignorées (D190).
 * Retourne null si aucune valeur mesurée.
 */
export function percentile(values, p) {
  const sorted = (Array.isArray(values) ? values : [])
    .filter(isFiniteNumber)
    .sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * Min / max / moyenne d'une grandeur. null et non finis ignorés : une mesure
 * absente ne devient jamais 0 (D190). Aucune valeur → stats null.
 * @returns {{ min: number|null, max: number|null, avg: number|null }}
 */
export function computeMinMaxAvg(values) {
  const list = Array.isArray(values) ? values : [];
  let count = 0;
  let sum = 0;
  let min = null;
  let max = null;
  for (const value of list) {
    if (!isFiniteNumber(value)) continue;
    if (min === null || value < min) min = value;
    if (max === null || value > max) max = value;
    sum += value;
    count += 1;
  }
  if (count === 0) return { min: null, max: null, avg: null };
  return { min, max, avg: sum / count };
}

const DEFAULT_STEP_MIN = 1;

// Instant d'une mesure en ms : Date (serveur) ou chaîne ISO (réponse JSON).
function timeMs(timestamp) {
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp === 'string') return Date.parse(timestamp);
  return NaN;
}

/**
 * Pas d'échantillonnage en minutes, même règle que sessionStatsEnricher :
 * intervalle d'acquisition déclaré (secondes) s'il est fourni, sinon médiane
 * des écarts positifs entre mesures consécutives (D198 : une coupure ne gonfle
 * pas le pas), sinon 1 min. Mesures attendues dans l'ordre chronologique.
 * @param {Array<{timestamp}>} readings
 * @param {number} [acquisitionIntervalSec] — session.mobileMetadata.acquisitionInterval
 */
export function computeStepMin(readings, acquisitionIntervalSec) {
  if (isFiniteNumber(acquisitionIntervalSec) && acquisitionIntervalSec > 0) {
    return acquisitionIntervalSec / 60;
  }
  const list = Array.isArray(readings) ? readings : [];
  const deltas = [];
  for (let i = 1; i < list.length; i += 1) {
    const dt = timeMs(list[i]?.timestamp) - timeMs(list[i - 1]?.timestamp);
    if (dt > 0) deltas.push(dt);
  }
  return deltas.length > 0 ? percentile(deltas, 50) / 60000 : DEFAULT_STEP_MIN;
}

/**
 * Heures au-dessus d'un seuil (strictement) : nombre de mesures × pas / 60,
 * arrondi à 0,1 h comme le serveur. null si aucune valeur mesurée : « 0 h
 * au-dessus » n'est jamais affirmé sans mesure.
 * @param {Array<Object>} readings
 * @param {string} field — ex. 'pm2_5'
 * @param {number} threshold
 * @param {number} [stepMin] — pas précalculé, sinon computeStepMin(readings)
 */
export function hoursAbove(readings, field, threshold, stepMin = computeStepMin(readings)) {
  let above = 0;
  let measured = 0;
  for (const r of Array.isArray(readings) ? readings : []) {
    const value = r?.[field];
    if (!isFiniteNumber(value)) continue;
    measured += 1;
    if (value > threshold) above += 1;
  }
  if (measured === 0) return null;
  return Math.round((above * stepMin / 60) * 10) / 10;
}
